import { Render } from '@measured/puck';
import type { ReactNode } from 'react';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { Widget } from '@/components/widget';
import { useExternalConfig } from '@/features/populate/hooks/use-external-config';
import { useObjectJson } from '@/hooks/use-object-json';
import '@measured/puck/puck.css';

export const PUCK_PREVIEW_WIDGET_ID = 'PUCK_PREVIEW_WIDGET';

export interface PuckPreviewWidgetProps {
  container: string;
  uuid: string;
}

const TAILWIND_CDN_URL = 'https://cdn.tailwindcss.com';

export function PuckPreviewWidget({ container, uuid }: PuckPreviewWidgetProps) {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = contentRef.current;
    if (!root) return;

    const applyPreviewMode = (el: Element) => {
      if (el.tagName.toLowerCase() === 'dyta-dynamic-component') {
        el.setAttribute('mode', 'preview');
      }
      el.querySelectorAll('dyta-dynamic-component').forEach(child => {
        child.setAttribute('mode', 'preview');
      });
    };

    applyPreviewMode(root);

    const observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        for (const node of Array.from(mutation.addedNodes)) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            applyPreviewMode(node as Element);
          }
        }
      }
    });

    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (!document.head.querySelector(`script[src="${TAILWIND_CDN_URL}"]`)) {
      const script = document.createElement('script');
      script.src = TAILWIND_CDN_URL;
      script.async = true;
      document.head.appendChild(script);
    }

    const script2 = document.createElement('script');
    //script2.src = 'https://components.dyta.app/index-DakLUNxJ.js';
    script2.src = import.meta.env.VITE_DYTA_COMPONENTS_URL ?? 'https://components.dyta.app/index-CZ_zC4t5.js';
    //script2.src = 'https://components.dyta.app/index-C3xO-wvx.js';
    script2.async = true;
    document.head.appendChild(script2);

    // document.defaultView!.DYTA_CONFIG = {
    //   apiBaseUrl: 'https://api-public-f3hdazfpdxfegkf4.germanywestcentral-01.azurewebsites.net/api',
    //   identityServerUrl: 'https://dyta-keycloak-5-ezfkbbcheve6ajda.germanywestcentral-01.azurewebsites.net/realms/dyta',
    //   clientId: 'universal-integration-dev',
    //   defaultContainerUuid: '5f809fb0-23ed-4a93-80a4-ddaea739daaa',
    // };

    document.defaultView!.DYTA_CONFIG = {
      apiBaseUrl: import.meta.env.VITE_DYTA_CONFIG_API_BASE_URL,
      identityServerUrl: import.meta.env.VITE_DYTA_CONFIG_IDENTITY_SERVER_URL,
      clientId: import.meta.env.VITE_DYTA_CONFIG_CLIENT_ID,
      defaultContainerUuid: import.meta.env.VITE_DYTA_CONFIG_DEFAULT_CONTAINER_UUID,
    };

    document.defaultView!.DYTA_CONFIG_CS = {
      apiBaseUrl: import.meta.env.VITE_DYTA_CONFIG_CS_API_BASE_URL,
      identityServerUrl: import.meta.env.VITE_DYTA_CONFIG_CS_IDENTITY_SERVER_URL,
      clientId: import.meta.env.VITE_DYTA_CONFIG_CS_CLIENT_ID,
      defaultContainerUuid: import.meta.env.VITE_DYTA_CONFIG_CS_DEFAULT_CONTAINER_UUID,
    };

    /*
    document.defaultView!.DYTA_CONFIG = {
      apiBaseUrl: 'https://api.dyta.app/api',
      identityServerUrl: 'https://identity-2.dyta.app/realms/graph',
      clientId: 'graph-react-local',
      defaultContainerUuid: 'f9d9029f-d605-4bf9-94a2-a79cd88d7656',
    };
    document.defaultView!.DYTA_CONFIG_CS = {
      apiBaseUrl: 'https://api.dyta.app/api',
      identityServerUrl: 'https://identity-2.dyta.app/realms/graph',
      clientId: 'graph-react-local',
      defaultContainerUuid: 'f9d9029f-d605-4bf9-94a2-a79cd88d7656',
    };
    */

    const container = document.createElement('dyta-master');
    document.body.appendChild(container);
  }, []);

  const config = useExternalConfig();
  const {
    data: objectJsonData,
    isLoading,
    error,
  } = useObjectJson(uuid, container);

  let content: ReactNode;

  if (!container || !uuid) {
    content = (
      <div className="p-4 text-gray-500 dark:text-gray-400">
        Missing container or uuid. Use ?container=...&uuid=... in the URL.
      </div>
    );
  } else if (error) {
    content = (
      <div className="p-4 text-red-600 dark:text-red-400">
        Failed to load preview: {(error as Error).message}
      </div>
    );
  } else if (isLoading || !config) {
    content = (
      <div className="flex items-center justify-center p-8">
        <div
          className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent"
          role="status"
        />
      </div>
    );
  } else {
    let puckData = {
      root: {} as Record<string, unknown>,
      content: [],
      zones: {},
    };

    const jsonData = (objectJsonData as { data?: string } | undefined)?.data;
    if (jsonData) {
      try {
        const parsed = JSON.parse(jsonData) as {
          data?: { content?: typeof puckData };
        };
        puckData = parsed?.data?.content ?? puckData;
        content = <Render config={config.editor} data={puckData} />;
      } catch {
        content = (
          <div className="p-4 text-red-600 dark:text-red-400">
            Invalid JSON data
          </div>
        );
      }
    } else {
      content = <Render config={config.editor} data={puckData} />;
    }
  }

  return (
    <Widget id={PUCK_PREVIEW_WIDGET_ID} title="Preview">
      <div ref={contentRef}>{content}</div>
    </Widget>
  );
}
